import axios from "axios";
import { auth } from "../firebase";

/**
 * Backend API base URL — the one place it is decided.
 * REACT_APP_API_URL first (production: https://api.tinyglobalop.com), then
 * REACT_APP_LOGISTICS_API_URL; empty = same origin (local dev → CRA proxy to localhost:4000).
 */
export const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_LOGISTICS_API_URL || "";

/**
 * Logistics API utility (Node.js - port 4000)
 */
const api = axios.create({
    baseURL: API_BASE_URL,
});

// Identical GETs already in flight share one network request — e.g. two components on a page
// asking for the same list as they mount. Keyed by URL, params and the signed-in token, so
// different users never share. Requests that can be cancelled or download files go alone.
const inflightGets = new Map();
const network = axios.getAdapter(api.defaults.adapter);
api.defaults.adapter = (config) => {
    if ((config.method || "get").toLowerCase() !== "get" || config.signal || config.cancelToken || config.responseType === "blob") {
        return network(config);
    }
    const key = [config.baseURL, config.url, JSON.stringify(config.params || {}), config.headers?.Authorization || ""].join(" ");
    if (inflightGets.has(key)) return inflightGets.get(key);
    const request = network(config).finally(() => inflightGets.delete(key));
    inflightGets.set(key, request);
    return request;
};

// authStateReady() resolves after Firebase has finished reading from persistence.
// The old onAuthStateChanged pattern fired with null first (before indexedDB was read),
// causing auth.currentUser to be null on the first API call.
const authReady = auth.authStateReady();

// Request Interceptor: Attach Firebase ID Token
api.interceptors.request.use(
    async (config) => {
        await authReady; // wait for Firebase session restore before reading currentUser
        const user = auth.currentUser;
        if (user) {
            try {
                const token = await user.getIdToken();
                config.headers.Authorization = `Bearer ${token}`;
            } catch (error) {
                console.error("❌ Failed to get Firebase ID Token:", error);
            }
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response Interceptor: Global Error Handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            console.warn("🔒 Unauthorized access - redirecting to login or handling session...");
            // Optional: window.location.href = "/login";
        }
        return Promise.reject(error);
    }
);

export default api;
